import resources from '@doar/shared/Text/langData';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Suspense, lazy, useEffect } from 'react'; // เก็บ useEffect ไว้ก่อน เผื่อใช้ในอนาคต
import { I18nextProvider, initReactI18next } from 'react-i18next';
import {
    Route,
    BrowserRouter as Router,
    Routes,
    Navigate,
} from 'react-router-dom'; // ✅ เพิ่ม Navigate
import CookieConsentPopup from './components/cookie-consent/CookieConsentPopup';
import Preloader from './components/preloader';
import { FirebaseProvider } from './context/firebaseContext';
import './global.css';
import NewsAndEvent from './pages/news-and-event';
import TreatmentRecords from './pages/treatment-records';
import AuthService from './services/auth.service'; // ✅ ยังคงเก็บไว้ แต่จะคอมเมนต์ส่วนที่ถูกแทนที่โดย i18n ออกไป

// Classic Pages (Lazy Loading)
const AboutUs = lazy(() => import('./pages/aboutus'));
const Default = lazy(() => import('./pages/default'));
const Home = lazy(() => import('./pages/home'));
const ProfilePrev = lazy(() => import('./pages/profile-prev'));
const Profile = lazy(() => import('./pages/profile'));
const Questionnaire = lazy(() => import('./pages/questionnaire'));
const Record = lazy(() => import('./pages/record'));
const Emergency = lazy(() => import('./pages/emergency'));
const MakeAppointments = lazy(() => import('./pages/make-appointment'));
const Appointments = lazy(() => import('./pages/appointment'));
const Track = lazy(() => import('./pages/track'));
const HCList = lazy(() => import('./pages/hosp-clin-list'));
const Graph = lazy(() => import('./pages/graph'));
const ProfileAdmin = lazy(() => import('./pages/profile-admin'));
const SignIn = lazy(() => import('./pages/signin'));
const SignUp = lazy(() => import('./pages/signup'));
const VerifyAccount = lazy(() => import('./pages/verify-account'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const ErrorNotFound = lazy(() => import('./pages/error-404'));
const ClassicPlusErrorNotFound = lazy(
    () => import('./pages/classic-plus/error-404')
);

// ✅ การตั้งค่า i18n
void i18n
    .use(LanguageDetector) // 1. บอก i18next ให้ใช้ LanguageDetector
    .use(initReactI18next)
    .init({
        resources,
        // 2. ลบ 'lng' ออก เพราะ LanguageDetector จะจัดการให้
        // lng: 'en', // ไม่จำเป็นแล้ว, LanguageDetector จะกำหนดภาษาเริ่มต้นให้
        fallbackLng: 'en', // 3. เพิ่ม fallbackLng เป็น 'en' กรณีที่หาภาษาจาก detector ไม่เจอ
        debug: process.env.NODE_ENV === 'development', // 4. ตั้ง debug เป็น true เฉพาะในโหมด development
        interpolation: {
            escapeValue: false, // react already safes from xss
        },
        detection: {
            // 5. ตั้งค่า LanguageDetector
            order: ['localStorage', 'navigator'], // ลำดับการตรวจจับ: localStorage ก่อน, ถ้าไม่มีให้ใช้ภาษาของเบราว์เซอร์
            caches: ['localStorage'], // บันทึกภาษาที่เลือกไว้ใน localStorage
        },
    });

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
    const isAuthenticated = AuthService.isLogin(); // สมมติว่า AuthService.isLogin() ทำงานได้ถูกต้อง
    if (!isAuthenticated) {
        return <Navigate to="/signin" replace />;
    }
    return children;
};

const App = () => {
    return (
        <I18nextProvider i18n={i18n}>
            <Router>
                <FirebaseProvider>
                    <Suspense fallback={<Preloader />}>
                        <Routes>
                            {/* 1. Routes ที่ไม่ต้องการการยืนยันตัวตน (เช่น หน้าเข้าสู่ระบบ, สมัครสมาชิก, ข้อมูลสาธารณะ) */}
                            <Route path="/signin" element={<SignIn />} />
                            <Route path="/signup" element={<SignUp />} />
                            <Route
                                path="/verify-account"
                                element={<VerifyAccount />}
                            />
                            <Route
                                path="/forgot-password"
                                element={<ForgotPassword />}
                            />
                            <Route path="/aboutus" element={<AboutUs />} />
                            <Route
                                path="/news-and-event"
                                element={<NewsAndEvent />}
                            />
                            {/* หน้า Error เฉพาะ, หากคุณมีลิงก์ตรงไปที่หน้าเหล่านี้ */}
                            <Route
                                path="/error-404"
                                element={<ErrorNotFound />}
                            />
                            <Route
                                path="/classic-plus/error-404"
                                element={<ClassicPlusErrorNotFound />}
                            />

                            {/* 2. Routes ที่ต้องการการยืนยันตัวตน (ห่อด้วย PrivateRoute) */}
                            {/* ตรวจสอบให้แน่ใจว่า Path เหล่านี้เป็น Path ที่เฉพาะเจาะจง และอยู่ *ก่อน* Wildcard Route */}
                            <Route
                                path="/"
                                element={
                                    <PrivateRoute>
                                        <Default />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/home"
                                element={
                                    <PrivateRoute>
                                        <Home />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/profile-prev"
                                element={
                                    <PrivateRoute>
                                        <ProfilePrev />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/profile"
                                element={
                                    <PrivateRoute>
                                        <Profile />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/edit-profile"
                                element={
                                    <PrivateRoute>
                                        <Profile defaultDisplay={false} />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/my-appointments/:pName/:pID"
                                element={
                                    <PrivateRoute>
                                        <Record />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/make-appointment"
                                element={
                                    <PrivateRoute>
                                        <MakeAppointments />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/appointment"
                                element={
                                    <PrivateRoute>
                                        <Appointments />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/treatment-records"
                                element={
                                    <PrivateRoute>
                                        <TreatmentRecords />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/track"
                                element={
                                    <PrivateRoute>
                                        <Track />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/hospitals-clinics-list"
                                element={
                                    <PrivateRoute>
                                        <HCList />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/graph/:tab"
                                element={
                                    <PrivateRoute>
                                        <Graph />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/graph"
                                element={
                                    <PrivateRoute>
                                        <Graph />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/questionnaire"
                                element={
                                    <PrivateRoute>
                                        <Questionnaire />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/admin-profile"
                                element={
                                    <PrivateRoute>
                                        <ProfileAdmin />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="/emergency"
                                element={
                                    <PrivateRoute>
                                        <Emergency />
                                    </PrivateRoute>
                                }
                            />

                            {/* 3. Wildcard/Catch-all Route (ต้องอยู่ท้ายสุดเสมอ) */}
                            {/* Route นี้จะทำงานก็ต่อเมื่อไม่มี Route อื่นๆ ด้านบนที่ตรงกับ URL นั้นๆ */}
                            <Route
                                path="*"
                                element={<Navigate to="/home" replace />}
                            />
                        </Routes>
                    </Suspense>
                </FirebaseProvider>
            </Router>
            <CookieConsentPopup />
        </I18nextProvider>
    );
};

export default App;
